import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Video, CalendarDays, Clock, ExternalLink } from 'lucide-react';

import type { HomeNextSession } from '@/lib/api/home';

function fmt(ms: number): string {
  if (ms <= 0) return 'now';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  return h < 24 ? `in ${h}h ${m % 60}m` : `in ${Math.floor(h / 24)}d`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (isToday) return `Today, ${time}`;
  return `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}, ${time}`;
}

/** Staggered squares that sit just left of the illustration, per the design. */
const DOTS = [
  { right: 342, top: 20 }, { right: 360, top: 50 }, { right: 378, top: 80 },
  { right: 342, top: 110 }, { right: 360, top: 140 }, { right: 378, top: 170 },
];

export function NextSessionCard({ session, batchId }: { session: HomeNextSession | null; batchId?: string }) {
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  if (!session || !session.scheduled_at) return null;

  const start = new Date(session.scheduled_at).getTime();
  const diff = start - now;
  const isLive = diff <= 0 && diff > -2 * 60 * 60 * 1000;
  const hasRecording = !!session.recording_url;

  const goToTab = (tab: 'overview' | 'recording') => {
    if (batchId && session.topic_id) {
      navigate({ to: '/courses/$batchId/topics/$topicId', params: { batchId, topicId: session.topic_id }, search: { tab } });
    } else {
      navigate({ to: '/schedule' });
    }
  };

  return (
    <div
      className="relative flex flex-col items-start justify-between gap-6 overflow-hidden rounded-[24px] border border-[#f5e8e0] px-6 py-8 lg:flex-row lg:items-center lg:px-[41px] lg:py-[37px]"
      style={{
        backgroundImage:
          'linear-gradient(155.86deg, #fff8f6 7.735%, #fff4fb 45.774%, #fff7f3 92.265%)',
      }}
    >
      {/* Decorative confetti */}
      <div className="pointer-events-none absolute inset-0 hidden overflow-hidden lg:block" aria-hidden="true">
        {DOTS.map((d) => (
          <span
            key={`${d.right}-${d.top}`}
            className="absolute h-1 w-1 rounded-[2px] bg-[#ff5a1f] opacity-15"
            style={{ right: d.right, top: d.top }}
          />
        ))}
      </div>

      {/* Left: copy */}
      <div className="relative z-10 flex min-w-0 flex-col items-start">
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 shrink-0 rounded-[4px] ${isLive ? 'animate-pulse bg-[#22c55e]' : 'bg-[#ff5a1f]'}`}
          />
          <span
            className={`text-[10px] font-bold uppercase leading-[15px] tracking-[1.2px] ${isLive ? 'text-[#16a34a]' : 'text-[#ff5a1f]'}`}
          >
            {isLive ? 'Live Now' : 'Your Next Action'}
          </span>
        </div>

        <h2 className="pt-[14px] text-[32px] font-extrabold leading-[1.1] tracking-[-1px] text-[#111827] lg:text-[42px] lg:leading-[46.2px]">
          Join Live Session
        </h2>

        <p className="pt-2 text-[16px] font-medium leading-[24px] text-[#374151]">
          {session.title || 'Live Session'}
        </p>

        {session.batch_name && (
          <p className="pt-1 text-[13px] leading-[19.5px] text-[#9ca3af]">{session.batch_name}</p>
        )}

        <div className="flex flex-wrap items-center gap-4 pt-6">
          <span className="flex items-center gap-[6px] text-[13px] leading-[19.5px] text-[#6b7280]">
            <CalendarDays className="h-[14px] w-[14px] shrink-0" />
            {fmtDate(session.scheduled_at)}
          </span>
          <span className="hidden h-[14px] w-px shrink-0 bg-[#e5e7eb] sm:block" />
          <span className="flex items-center gap-[6px] text-[13px] leading-[19.5px] text-[#6b7280]">
            <Clock className="h-[14px] w-[14px] shrink-0" />
            {isLive ? 'In progress' : `Starts ${fmt(diff)}`}
          </span>
        </div>
      </div>

      {/* Middle: actions */}
      <div className="relative z-10 flex shrink-0 items-center gap-3">
        {/* Recording — active only when a recording exists */}
        {hasRecording ? (
          <a
            href={session.recording_url}
            target="_blank"
            rel="noreferrer"
            aria-label="Watch recording"
            className="flex h-[54px] w-[54px] items-center justify-center rounded-[14px] border border-[#e5e7eb] bg-white text-[#ff5a1f] shadow-[0_2px_4px_rgba(0,0,0,0.06)] transition-colors hover:bg-[#fff4f0]"
          >
            <Video className="h-5 w-5" />
          </a>
        ) : (
          <span
            aria-label="Recording not available"
            className="flex h-[54px] w-[54px] cursor-not-allowed items-center justify-center rounded-[14px] border border-[#e5e7eb] bg-white text-[#9ca3af] shadow-[0_2px_4px_rgba(0,0,0,0.06)]"
          >
            <Video className="h-5 w-5" />
          </span>
        )}

        {/* Join Session — always enabled; opens the meeting if a URL exists,
            otherwise jumps to the lesson's recording tab. */}
        {session.meeting_url ? (
          <a
            href={session.meeting_url}
            target="_blank"
            rel="noreferrer"
            className="flex h-[54px] w-[200px] items-center justify-center rounded-[14px] text-[15px] font-semibold tracking-[-0.2px] text-white shadow-[0_4px_8px_rgba(255,90,31,0.35)] transition-opacity hover:opacity-90"
            style={{ backgroundImage: 'linear-gradient(164.89deg, #ff6b3d 0%, #ff4d1a 100%)' }}
          >
            Join Session
          </a>
        ) : (
          <button
            onClick={() => goToTab('recording')}
            className="flex h-[54px] w-[200px] items-center justify-center rounded-[14px] text-[15px] font-semibold tracking-[-0.2px] text-white shadow-[0_4px_8px_rgba(255,90,31,0.35)] transition-opacity hover:opacity-90"
            style={{ backgroundImage: 'linear-gradient(164.89deg, #ff6b3d 0%, #ff4d1a 100%)' }}
          >
            Join Session
          </button>
        )}

        {/* View Details — navigates to the lesson overview tab */}
        <button
          onClick={() => goToTab('overview')}
          className="flex h-[54px] items-center gap-[6px] rounded-[14px] border border-[#e5e7eb] bg-white px-[21px] text-[14px] font-medium text-[#374151] shadow-[0_2px_4px_rgba(0,0,0,0.04)] transition-colors hover:bg-[#f9fafb]"
        >
          <ExternalLink className="h-[14px] w-[14px] shrink-0" />
          View Details
        </button>
      </div>

      {/* Right: illustration */}
      <img
        src="/images/StudentIllustration.svg"
        alt=""
        aria-hidden="true"
        width={220}
        height={220}
        className="pointer-events-none relative z-10 hidden h-[220px] w-[220px] shrink-0 select-none lg:block"
      />
    </div>
  );
}
